/**
 * API client for the CRM backend (crm.leadspoint.in).
 *
 * The console talks to two servers. Identity Center (api/axios.js) owns
 * orgs, users and billing; the CRM backend owns telecom providers, customer
 * reviews, LOB templates and field options. Those screens used to call CRM
 * paths on Identity Center and got 404.
 *
 * Same login token: the CRM backend accepts admin-console tokens as platform
 * operators (django-platform-auth >= 0.2.0, OPERATOR_ROLE_MAP in the CRM's
 * settings: console owners and developers). No CRM user is involved.
 * A 401/403 here does NOT end the console session; it means this console
 * role has no CRM access, and CrmAccessGate says so.
 */
import axios from 'axios';
import { getAccessToken } from '@/utils/authStorage';

export const CRM_API_URL = (import.meta.env.VITE_CRM_API_URL || 'https://crm.leadspoint.in').replace(/\/$/, '');

const crmAxios = axios.create({
  baseURL: CRM_API_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 15000,
  headers: { Accept: 'application/json' },
  withCredentials: false,
});

// These screens were copied from the CRM's own superadmin app, which called
// organizations and org-LOB links at /api/…; on the CRM backend they live
// under /api/accounts/… (accounts/urls.py). Map them here, in one place.
const PATH_MAP = [
  [/^\/api\/organizations(\/|$)/, '/api/accounts/organizations$1'],
  [/^\/api\/organization-lobs(\/|$)/, '/api/accounts/organization-lobs$1'],
];

export const mapCrmPath = (url) => {
  if (typeof url !== 'string') return url;
  for (const [pattern, replacement] of PATH_MAP) {
    if (pattern.test(url)) return url.replace(pattern, replacement);
  }
  return url;
};

crmAxios.interceptors.request.use((config) => {
  config.url = mapCrmPath(config.url);
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (!(config.data instanceof FormData)) config.headers['Content-Type'] = 'application/json';
  return config;
});

crmAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Lets DataState explain CRM access problems instead of "sign-in expired".
    error.isCrm = true;
    return Promise.reject(error);
  }
);

export default crmAxios;
